import React, { useMemo } from 'react';

function parseBreakdownValue(v) {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (typeof v === 'object') return [];

    const s = String(v || '').trim();
    if (!s) return [];
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function parseJsonArrayLoose(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return [];
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function parseNumberLoose(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    // allow TBC / - / etc
    if (!/[0-9]/.test(s)) return null;
    const cleaned = s.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

function formatPounds(v) {
    if (v === null || v === undefined) return '—';
    const n = typeof v === 'number' ? v : parseNumberLoose(v);
    if (n === null) {
        const s = String(v || '').trim();
        return s ? s : '—';
    }
    return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

function makeCostItemKey(prefix, heading, index) {
    const h = String(heading || '').trim();
    if (h) return `${prefix}:${h}`;
    return `${prefix}:idx:${index + 1}`;
}

function normalizeKeyText(v) {
    return String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/[^a-z0-9 -]/g, '');
}

function truthyFromString(v) {
    const s = String(v ?? '').trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'on';
}

function parsePercentOrAmount(raw, baseAmount) {
    const s = String(raw ?? '').trim();
    if (!s) return { amount: null, percent: null };

    const base = parseNumberLoose(baseAmount);
    if (s.includes('%')) {
        const pct = parseNumberLoose(s);
        if (pct === null) return { amount: null, percent: null };
        const amt = base !== null ? (base * pct) / 100 : null;
        return { amount: amt, percent: pct };
    }

    const amt = parseNumberLoose(s);
    if (amt === null) return { amount: null, percent: null };
    const pct = base !== null && base !== 0 ? (amt / base) * 100 : null;
    return { amount: amt, percent: pct };
}

function round2(n) {
    if (!Number.isFinite(n)) return n;
    return Math.round(n * 100) / 100;
}

function formatMainPounds(value) {
    const raw = String(value ?? '').trim();
    if (raw.toUpperCase() === 'TBC') return formatPounds(0);
    const num = parseNumberLoose(value);
    return num === null ? '—' : formatPounds(num);
}

export function computeProjectCostSummary({ row, devGrossInternalArea, valuesByKey }) {
    const map = valuesByKey && typeof valuesByKey.get === 'function' ? valuesByKey : new Map();

    const constructionBreakdown = parseBreakdownValue(row?.construction_cost_breakdown);
    const professionalBreakdown = parseBreakdownValue(row?.professional_fees_breakdown);
    const otherBreakdown = parseBreakdownValue(row?.other_fees_breakdown);

    function getSurveyorValue(itemKey) {
        return map.get(String(itemKey)) ?? '';
    }

    const constructionBreakdownNums = constructionBreakdown
        .map((it) => parseNumberLoose(it?.amount))
        .filter((n) => n !== null);
    const constructionBreakdownSum = constructionBreakdownNums.length
        ? constructionBreakdownNums.reduce((a, b) => a + b, 0)
        : null;
    const borrowerConstructionTotal = constructionBreakdownSum ?? parseNumberLoose(row?.build_cost) ?? 0;

    const borrowerProfessionalTotal = parseNumberLoose(row?.professional_fees) ?? (
        professionalBreakdown
            .map((it) => parseNumberLoose(it?.amount))
            .filter((n) => n !== null)
            .reduce((a, b) => a + b, 0)
    );

    const borrowerOtherTotal = otherBreakdown
        .map((it) => parseNumberLoose(it?.amount))
        .filter((n) => n !== null)
        .reduce((a, b) => a + b, 0);

    const borrowerCont = parsePercentOrAmount(row?.contingency, borrowerConstructionTotal);

    // Surveyor values / derived totals
    const facilityConfirmedRaw = getSurveyorValue('facility_confirmed');
    const facilityConfirmed = truthyFromString(facilityConfirmedRaw);
    const facilityAmountStored = getSurveyorValue('facility_amount');
    const facilityAmountDisplay = facilityConfirmed ? facilityAmountStored : 'TBC';
    const facilityAmountNum = parseNumberLoose(facilityAmountDisplay);

    const giaSurveyorRaw = getSurveyorValue('proposed_gia');
    const giaSurveyorM2 = parseNumberLoose(giaSurveyorRaw);
    const effectiveGiaM2 = giaSurveyorM2;
    const effectiveFt2 = effectiveGiaM2 === null ? null : effectiveGiaM2 * 10.7639;

    function isBespokeKey(itemKey) {
        return `construction_bespoke:${String(itemKey)}`;
    }

    const bespokeTotals = constructionBreakdown.reduce(
        (acc, it, idx) => {
            const itemKey = makeCostItemKey('construction_cost_breakdown', it?.heading, idx);
            const isBespoke = truthyFromString(getSurveyorValue(isBespokeKey(itemKey)));
            const amount = parseNumberLoose(it?.amount) ?? 0;
            if (isBespoke) acc.bespoke += amount;
            else acc.construction += amount;
            return acc;
        },
        { bespoke: 0, construction: 0 }
    );

    const developerConstructionRateFt2 = effectiveFt2 ? round2(bespokeTotals.construction / effectiveFt2) : null;
    const surveyorConstructionRateRaw = getSurveyorValue('construction_items_rate_ft2');
    const surveyorConstructionRateFt2 = (() => {
        const v = parseNumberLoose(surveyorConstructionRateRaw);
        return v === null ? null : round2(v);
    })();

    const bespokeRateFt2 = effectiveFt2 ? round2(bespokeTotals.bespoke / effectiveFt2) : null;
    let surveyorConstructionTotal = (effectiveFt2 && surveyorConstructionRateFt2 !== null)
        ? (round2(surveyorConstructionRateFt2 + (bespokeRateFt2 || 0)) * effectiveFt2)
        : null;

    const ratesMatch = (
        borrowerConstructionTotal !== null &&
        developerConstructionRateFt2 !== null &&
        surveyorConstructionRateFt2 !== null &&
        round2(developerConstructionRateFt2) === round2(surveyorConstructionRateFt2)
    );
    if (ratesMatch) surveyorConstructionTotal = borrowerConstructionTotal;

    // Professional fees surveyor total
    const professionalChildKeys = professionalBreakdown.map((it, idx) =>
        makeCostItemKey('professional_fees_breakdown', it?.heading, idx)
    );
    const professionalChildNums = professionalChildKeys
        .map((k) => parseNumberLoose(getSurveyorValue(k)))
        .filter((n) => n !== null);
    const derivedSurveyorProfessional = professionalChildKeys.length
        ? (professionalChildNums.length ? professionalChildNums.reduce((a, b) => a + b, 0) : null)
        : null;

    const professionalExtraRowIds = parseJsonArrayLoose(getSurveyorValue('professional_fees_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);
    const professionalExtraNums = professionalExtraRowIds
        .map((id) => parseNumberLoose(getSurveyorValue(`professional_fee_extra:${id}:amount`)))
        .filter((n) => n !== null);
    const professionalExtraSum = professionalExtraNums.length ? professionalExtraNums.reduce((a, b) => a + b, 0) : null;

    const surveyorProfessionalBase = professionalChildKeys.length
        ? derivedSurveyorProfessional
        : parseNumberLoose(getSurveyorValue('professional_fees'));
    const surveyorProfessionalTotal = (surveyorProfessionalBase === null && professionalExtraSum === null)
        ? null
        : ((surveyorProfessionalBase || 0) + (professionalExtraSum || 0));

    // Other costs surveyor total
    function otherLineKey(label) {
        return `other_cost:${normalizeKeyText(label).replace(/\s/g, '_')}`;
    }

    const otherLines = (() => {
        const seen = new Set();
        const items = [];
        (Array.isArray(otherBreakdown) ? otherBreakdown : []).forEach((it) => {
            const heading = String(it?.heading || '').trim();
            if (!heading) return;
            const k = normalizeKeyText(heading);
            if (!k || seen.has(k)) return;
            seen.add(k);
            items.push(heading);
        });
        if (!seen.has(normalizeKeyText('Monitoring Surveyor'))) items.push('Monitoring Surveyor');
        return items;
    })();

    const otherSurveyorNums = otherLines
        .map((label) => parseNumberLoose(getSurveyorValue(otherLineKey(label))))
        .filter((n) => n !== null);

    const otherExtraRowIds = parseJsonArrayLoose(getSurveyorValue('other_costs_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);
    const otherExtraNums = otherExtraRowIds
        .map((id) => parseNumberLoose(getSurveyorValue(`other_cost_extra:${id}:amount`)))
        .filter((n) => n !== null);

    const otherBaseSum = otherSurveyorNums.length ? otherSurveyorNums.reduce((a, b) => a + b, 0) : null;
    const otherExtraSum = otherExtraNums.length ? otherExtraNums.reduce((a, b) => a + b, 0) : null;
    const surveyorOtherTotal = (otherBaseSum === null && otherExtraSum === null)
        ? null
        : ((otherBaseSum || 0) + (otherExtraSum || 0));

    const surveyorCont = parsePercentOrAmount(getSurveyorValue('contingency'), surveyorConstructionTotal);

    const borrowerSubtotal = [borrowerConstructionTotal, borrowerCont.amount, borrowerProfessionalTotal, borrowerOtherTotal]
        .map((n) => (typeof n === 'number' ? n : parseNumberLoose(n)))
        .filter((n) => n !== null)
        .reduce((a, b) => a + b, 0);

    const surveyorSubtotalParts = [surveyorConstructionTotal, surveyorCont.amount, surveyorProfessionalTotal, surveyorOtherTotal]
        .map((n) => (typeof n === 'number' ? n : parseNumberLoose(n)))
        .filter((n) => n !== null);
    const surveyorSubtotal = surveyorSubtotalParts.length ? surveyorSubtotalParts.reduce((a, b) => a + b, 0) : null;

    const borrowerInputSurveyorRaw = getSurveyorValue('less_borrower_input');
    const borrowerInputSurveyorNum = parseNumberLoose(borrowerInputSurveyorRaw);
    const facilityAmountForCalc = facilityAmountNum ?? 0;
    const borrowerInputForCalc = borrowerInputSurveyorNum ?? 0;
    const headroomSurveyor = surveyorSubtotal === null
        ? null
        : surveyorSubtotal - facilityAmountForCalc - borrowerInputForCalc;

    return {
        borrowerConstructionTotal,
        borrowerCont,
        borrowerProfessionalTotal,
        borrowerOtherTotal,
        borrowerSubtotal,
        facilityAmountDisplay,
        surveyorConstructionTotal,
        surveyorCont,
        surveyorProfessionalTotal,
        surveyorOtherTotal,
        surveyorSubtotal,
        borrowerInputSurveyorRaw,
        headroomSurveyor,
        devGIA: parseNumberLoose(devGrossInternalArea),
        surveyorGIA: giaSurveyorM2
    };
}

export function computeSCurveBaseline({ row, devGrossInternalArea, valuesByKey }) {
    if (!row) return { amount: null, source: null };

    const summary = computeProjectCostSummary({ row, devGrossInternalArea, valuesByKey });

    const surveyorConstruction = (typeof summary?.surveyorConstructionTotal === 'number') ? summary.surveyorConstructionTotal : null;
    const surveyorContingency = (typeof summary?.surveyorCont?.amount === 'number') ? summary.surveyorCont.amount : 0;

    if (surveyorConstruction !== null) {
        const baseline = surveyorConstruction + (surveyorContingency || 0);
        return { amount: baseline > 0 ? baseline : null, source: baseline > 0 ? 'surveyor' : null };
    }

    // Fallback: if surveyor values aren't present yet, use developer (borrower) totals.
    const devConstruction = (typeof summary?.borrowerConstructionTotal === 'number') ? summary.borrowerConstructionTotal : null;
    const devContingency = (typeof summary?.borrowerCont?.amount === 'number') ? summary.borrowerCont.amount : 0;
    if (devConstruction === null) return { amount: null, source: null };

    const devBaseline = devConstruction + (devContingency || 0);
    return { amount: devBaseline > 0 ? devBaseline : null, source: devBaseline > 0 ? 'developer' : null };
}

export default function ProjectCostsSummaryTable({
    row,
    devGrossInternalArea,
    valuesByKey
}) {
    const computed = useMemo(() => {
        return computeProjectCostSummary({ row, devGrossInternalArea, valuesByKey });
    }, [row, devGrossInternalArea, valuesByKey]);

    if (!row) {
        return <div style={{ color: '#6b7280', fontSize: 13 }}>No cost table has been submitted for this project yet.</div>;
    }

    const headerRowBg = '#f3f4f6';
    const subtotalRowBg = '#f9fafb';

    const cell = { padding: '10px 10px', border: '1px solid #e5e7eb' };
    const cellLabel = { ...cell, fontWeight: 800 };
    const cellRight = { ...cell, textAlign: 'right', fontWeight: 700 };

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                <thead>
                    <tr style={{ background: headerRowBg }}>
                        <th style={{ ...cell, textAlign: 'left' }}>Description</th>
                        <th style={cell}>Developer Assessment</th>
                        <th style={cell}>Surveyor Assessment</th>
                        <th style={cell}>Difference</th>
                    </tr>
                </thead>
                <tbody>
                    <tr style={{ background: subtotalRowBg }}>
                        <td style={cellLabel}>Construction Costs</td>
                        <td style={cellRight}>{formatPounds(computed.borrowerConstructionTotal)}</td>
                        <td style={cellRight}>{computed.surveyorConstructionTotal === null ? '—' : formatPounds(computed.surveyorConstructionTotal)}</td>
                        <td style={cellRight}>{computed.surveyorConstructionTotal === null ? '—' : formatPounds(computed.surveyorConstructionTotal - computed.borrowerConstructionTotal)}</td>
                    </tr>

                    <tr style={{ background: subtotalRowBg }}>
                        <td style={cellLabel}>Contingency</td>
                        <td style={cellRight}>{computed.borrowerCont.amount === null ? '—' : formatPounds(computed.borrowerCont.amount)}</td>
                        <td style={cellRight}>{computed.surveyorCont.amount === null ? '—' : formatPounds(computed.surveyorCont.amount)}</td>
                        <td style={cellRight}>{computed.surveyorCont.amount === null ? '—' : formatPounds(computed.surveyorCont.amount - (computed.borrowerCont.amount || 0))}</td>
                    </tr>

                    <tr style={{ background: subtotalRowBg }}>
                        <td style={cellLabel}>Professional Fees</td>
                        <td style={cellRight}>{formatPounds(computed.borrowerProfessionalTotal)}</td>
                        <td style={cellRight}>{computed.surveyorProfessionalTotal === null ? '—' : formatPounds(computed.surveyorProfessionalTotal)}</td>
                        <td style={cellRight}>{computed.surveyorProfessionalTotal === null ? '—' : formatPounds(computed.surveyorProfessionalTotal - computed.borrowerProfessionalTotal)}</td>
                    </tr>

                    <tr style={{ background: subtotalRowBg }}>
                        <td style={cellLabel}>Other Costs</td>
                        <td style={cellRight}>{formatPounds(computed.borrowerOtherTotal)}</td>
                        <td style={cellRight}>{computed.surveyorOtherTotal === null ? '—' : formatPounds(computed.surveyorOtherTotal)}</td>
                        <td style={cellRight}>{computed.surveyorOtherTotal === null ? '—' : formatPounds(computed.surveyorOtherTotal - computed.borrowerOtherTotal)}</td>
                    </tr>

                    <tr style={{ background: subtotalRowBg }}>
                        <td style={{ ...cellLabel, fontWeight: 900 }}>Sub-total Development Cost</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(computed.borrowerSubtotal)}</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>{computed.surveyorSubtotal === null ? '—' : formatPounds(computed.surveyorSubtotal)}</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>{computed.surveyorSubtotal === null ? '—' : formatPounds(computed.surveyorSubtotal - computed.borrowerSubtotal)}</td>
                    </tr>

                    <tr>
                        <td style={cellLabel}>Less Development Facility</td>
                        <td style={cellRight}>{formatMainPounds(computed.facilityAmountDisplay)}</td>
                        <td style={cellRight}>{formatMainPounds(computed.facilityAmountDisplay)}</td>
                        <td style={cellRight}>{formatPounds(0)}</td>
                    </tr>

                    <tr>
                        <td style={cellLabel}>Less Borrower input</td>
                        <td style={cellRight}>{formatPounds(0)}</td>
                        <td style={cellRight}>{computed.borrowerInputSurveyorRaw ? formatPounds(computed.borrowerInputSurveyorRaw) : '—'}</td>
                        <td style={cellRight}>—</td>
                    </tr>

                    <tr style={{ background: headerRowBg }}>
                        <td style={{ ...cellLabel, fontWeight: 900 }}>Headroom / (Shortfall)</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(0)}</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>{computed.headroomSurveyor === null ? '—' : formatPounds(computed.headroomSurveyor)}</td>
                        <td style={{ ...cellRight, fontWeight: 900 }}>—</td>
                    </tr>
                </tbody>
            </table>

            {String(row?.cost_commentary || '').trim() && (
                <div style={{ marginTop: 10, color: '#374151', fontSize: 13 }}>
                    <strong>Cost Commentary (Developer):</strong> {String(row.cost_commentary)}
                </div>
            )}
        </div>
    );
}
